import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import type { ToastType } from "../../../components/UI/Toast/Toast";
import {
  Currency,
  MaterialResponse,
  PurchaseRequestItemType,
  UserCompanyDetail,
  UserCompanyType,
} from "../../../types";
import { useCreatePurchaseRequestV2 } from "../../../hooks/usePurchaseRequestsV2";
import { useFileInputUploader } from "../../../hooks";
import { useCreateMaterial } from "../../../hooks/useMaterials";
import { useSites } from "../../../hooks/useSites";
import { useUser, useNotification } from "../../../context";
import { useUsersByCompany } from "../../../hooks/useUsers";
import i18next from "../../../i18n";
import type { MaterialCreationType } from "./NewMaterialModal";
import { createGlobalHandlers } from "./requestGlobals";
import { createFileHandlers } from "./requestFiles";
import { isItemComplete } from "./itemStatus";
import { buildEmptyItem, generateClientId } from "./types";
import type { ItemRow as ItemRowType } from "./types";
import { materialPatch } from "./materialPatch";
import { buildCreatePayload } from "./buildCreatePayload";
import {
  emptyRequestFormValues,
  requestFormValidationSchema,
  type RequestFormValues,
} from "./requestFormSchema";
import {
  canComposerSubmit,
  emptyComposer,
  type ComposerState,
} from "./ItemComposer";

/** Where a just-created material should land once the API returns it. */
export type MaterialTarget =
  | { kind: "composer" }
  | { kind: "item"; clientId: string }
  // Top-level "Crear material": create a material and append it as a brand-new request item.
  | { kind: "newItem" };

/**
 * All state, mutations and handlers for the New-Purchase-Request page — split from the page (JSX)
 * so each file stays under budget. One `useForm<RequestFormValues>()` + `useFieldArray("items")` is
 * the whole store; the apply-to-all handlers (`requestGlobals`), file uploads (`requestFiles`),
 * material patch and import all write into that field array. `buildCreatePayload` produces the
 * untouched `POST /api/purchase-requests` body; `requestFormValidationSchema` is the submit gate.
 */
export const useNewPurchaseRequest = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { openNotification } = useNotification();
  const { data: sites } = useSites(user?.company?.id || "");
  const { data: companyUsers } = useUsersByCompany(user?.id || "");

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const lastSiteIdRef = useRef("");
  const pendingCreationType = useRef<MaterialCreationType>("GOOD");

  const { control, getValues, setValue, watch } = useForm<RequestFormValues>({
    defaultValues: emptyRequestFormValues,
  });
  const { fields, append, insert, remove, update } = useFieldArray({
    control,
    name: "items",
    keyName: "_fieldId",
  });

  const values = watch();
  // watch() returns a fresh object each render, so `items` can't be memoised into stability here;
  // the downstream useMemo guards its own recompute. (react-scripts build treats this warning as an error.)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = values.items ?? [];

  const [optionsOpen, setOptionsOpen] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [composer, setComposer] = useState<ComposerState>(emptyComposer());
  const [showConfirm, setShowConfirm] = useState(false);
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const [materialModalName, setMaterialModalName] = useState("");
  const [materialTarget, setMaterialTarget] = useState<MaterialTarget>({
    kind: "composer",
  });
  const [materialFileURL, setMaterialFileURL] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<ToastType>("info");
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  const todayString = new Date().toISOString().split("T")[0];

  const showNotification = (message: string, type: ToastType = "info") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const { mutate: createRequest, isPending: isCreating } =
    useCreatePurchaseRequestV2((res) => {
      // Global toast (NotificationProvider) — survives the navigate to the new PR,
      // which a page-local toast would not.
      openNotification(i18next.t("purchaseRequests.success"));
      navigate(`/purchase-requests/${res.id}`);
    });

  const { mutate: uploadMaterialSpec, isPending: isUploadingMaterialSpec } =
    useFileInputUploader((fileURL: string) => {
      if (fileURL) setMaterialFileURL(fileURL);
    });

  const { mutate: createMaterial, isPending: isCreatingMaterial } =
    useCreateMaterial(
      (material: MaterialResponse) => {
        applyCreatedMaterial(material);
        setMaterialModalOpen(false);
        setMaterialFileURL(null);
      },
      (error: any) => {
        const errorType = error?.response?.data?.errorType;
        showNotification(
          i18next.t(
            errorType === "DUPLICATED"
              ? "newMaterialForm.repeated"
              : "newMaterialForm.error"
          ),
          "error"
        );
      }
    );

  const { uploadFile, handleAttachmentUpload, handleRemoveFile } =
    createFileHandlers(getValues, setValue, setIsUploadingAttachment);

  const {
    handleGlobalBudgetValueChange,
    handleGlobalCurrencyChange,
    handleToggleGlobalBudget,
    handleGlobalNeededFromChange,
    handleGlobalNeededToChange,
    handleToggleGlobalDates,
    setSingleDateMode,
  } = createGlobalHandlers(getValues, setValue);

  const globalDatesForNewItem = () => {
    const v = getValues();
    if (!v.useGlobalDates) return { neededFrom: null, neededTo: null };
    if (v.singleDateMode) {
      const singleDate = v.globalNeededFrom || v.globalNeededTo || null;
      return { neededFrom: singleDate, neededTo: singleDate };
    }
    return {
      neededFrom: v.globalNeededFrom || null,
      neededTo: v.globalNeededTo || null,
    };
  };

  const setItemField = (index: number, field: keyof ItemRowType, value: any) =>
    setValue(`items.${index}.${field}` as any, value as any);

  const patchItem = (index: number, patch: Partial<ItemRowType>) =>
    update(index, {
      ...(getValues(`items.${index}`) as ItemRowType),
      ...patch,
    });

  const handleAddFromComposer = () => {
    const { material, quantity, measurementUnit } = composer;
    if (!canComposerSubmit(composer) || !material) return;
    const v = getValues();

    const base: ItemRowType = {
      ...buildEmptyItem(),
      currency: v.useGlobalBudget ? v.globalCurrency : Currency.USD,
      budgetCurrency: v.useGlobalBudget ? v.globalCurrency : Currency.USD,
      budgetValue: v.useGlobalBudget ? v.globalBudgetValue : null,
      ...globalDatesForNewItem(),
    };

    append({
      ...base,
      ...materialPatch(material, base),
      quantity,
      measurementUnit: measurementUnit || material.measurementUnit || "",
      site: { id: composer.siteId },
      address: "",
    });

    lastSiteIdRef.current = composer.siteId;
    setComposer(emptyComposer(composer.siteId));
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  /**
   * Build a fresh item seeded with the current request-level defaults (so it starts "inherited" —
   * its date/budget equal the request's and render greyed in the table until overridden). Shared by
   * `handleAddItem` and the create-material-then-add-item flow so the two start identically.
   */
  const buildSeededItem = (): ItemRowType => {
    const v = getValues();
    return {
      ...buildEmptyItem(),
      currency: v.useGlobalBudget ? v.globalCurrency : Currency.USD,
      budgetCurrency: v.useGlobalBudget ? v.globalCurrency : Currency.USD,
      budgetValue: v.useGlobalBudget ? v.globalBudgetValue : null,
      ...globalDatesForNewItem(),
    };
  };

  /** Append a seeded item and open it for editing. */
  const handleAddItem = () => {
    const base = buildSeededItem();
    append(base);
    setExpandedId(base.clientId ?? null);
  };

  const handleDuplicateRow = (index: number) => {
    const source = getValues(`items.${index}`) as ItemRowType;
    insert(index + 1, {
      ...source,
      clientId: generateClientId(),
      files: source.files ? [...source.files] : undefined,
    });
  };

  const handleToggleExpand = (clientId: string) => {
    setExpandedId((prev) => (prev === clientId ? null : clientId));
  };

  const handleMaterialSelected = (
    index: number,
    material: MaterialResponse
  ) => {
    const current = getValues(`items.${index}`) as ItemRowType;
    patchItem(index, materialPatch(material, current));
  };

  const handleClearMaterial = (index: number) => {
    patchItem(index, {
      material: null,
      materialEan: undefined,
      materialName: undefined,
      materialOwnerName: undefined,
      assignedBuyer: null,
      itemType: PurchaseRequestItemType.GOOD,
      purpose: null,
      brand: null,
    });
  };

  const openMaterialModal = (target: MaterialTarget, searchTerm: string) => {
    setMaterialTarget(target);
    setMaterialModalName(searchTerm);
    setMaterialFileURL(null);
    setMaterialModalOpen(true);
  };

  /**
   * Top-level "Crear material" action: open the create-material dialog with no search term. On
   * success the material is appended as a new request item (see `applyCreatedMaterial`'s `newItem`).
   */
  const openCreateMaterialModal = () =>
    openMaterialModal({ kind: "newItem" }, "");

  function applyCreatedMaterial(material: MaterialResponse) {
    const forcedType =
      pendingCreationType.current === "SERVICE"
        ? PurchaseRequestItemType.SERVICE
        : PurchaseRequestItemType.GOOD;

    if (materialTarget.kind === "composer") {
      setComposer((prev) => ({
        ...prev,
        material,
        measurementUnit: prev.measurementUnit || material.measurementUnit || "",
        siteId:
          material.deliveryPlace?.id || prev.siteId || lastSiteIdRef.current,
      }));
      return;
    }

    if (materialTarget.kind === "newItem") {
      // Top-level "Crear material": the created material becomes a brand-new request item, seeded
      // with the request defaults like any added item, then opened for review.
      const base = buildSeededItem();
      append({ ...base, ...materialPatch(material, base, forcedType) });
      setExpandedId(base.clientId ?? null);
      return;
    }

    const current = getValues("items");
    const idx = current.findIndex(
      (it) => it.clientId === materialTarget.clientId
    );
    if (idx >= 0) {
      update(idx, {
        ...current[idx],
        ...materialPatch(material, current[idx], forcedType),
      });
    }
  }

  const handleCreateMaterialSubmit = (
    payload: any,
    creationType: MaterialCreationType
  ) => {
    pendingCreationType.current = creationType;
    createMaterial(payload);
  };

  const completeCount = useMemo(
    () => items.filter(isItemComplete).length,
    [items]
  );
  const canSubmit = requestFormValidationSchema.isValidSync({
    name: values.name,
    items,
  });

  const handleJumpToIncomplete = () => {
    const target = items.find((item) => !isItemComplete(item));
    if (!target?.clientId) return;
    setExpandedId(target.clientId);
    document
      .getElementById(`item-${target.clientId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = (draft: boolean) => {
    const v = getValues();
    if (!v.name.trim()) {
      showNotification(i18next.t("purchaseRequests.nameRequired"), "warning");
      return;
    }
    const payload = buildCreatePayload(
      {
        name: v.name,
        purchaseRequestCode: v.purchaseRequestCode,
        comments: v.comments,
        files: v.files,
        items: v.items,
      },
      draft
    );
    setShowConfirm(false);
    createRequest(payload);
  };

  const buyerUsers: UserCompanyDetail[] = useMemo(
    () =>
      companyUsers?.filter(
        (u: UserCompanyDetail) =>
          (u.userCompanyType === UserCompanyType.BUYER ||
            u.userCompanyType === UserCompanyType.MANAGER ||
            u.userCompanyType === UserCompanyType.ANALYST) &&
          u.id !== user?.id
      ) || [],
    [companyUsers, user?.id]
  );

  const busy = isCreating || isUploadingAttachment;

  // The date a freshly-added (still inherited) item carries — the yardstick the items table uses
  // to grey out a row's date while it matches the request-level default.
  const globalItemDates = globalDatesForNewItem();

  return {
    control,
    sites: sites ?? [],
    buyerUsers,
    todayString,
    globalItemDates,
    values,
    items,
    fields,
    completeCount,
    canSubmit,
    busy,
    isCreating,
    isCreatingMaterial,
    isUploadingMaterialSpec,
    isUploadingAttachment,
    composer,
    setComposer,
    optionsOpen,
    setOptionsOpen,
    expandedId,
    showConfirm,
    setShowConfirm,
    materialModalOpen,
    setMaterialModalOpen,
    materialModalName,
    materialFileURL,
    setMaterialFileURL,
    showToast,
    setShowToast,
    toastMessage,
    toastType,
    navigate,
    openNotification,
    uploadFile,
    uploadMaterialSpec,
    setSearchInput: (el: HTMLInputElement | null) => {
      searchInputRef.current = el;
    },
    setItemField,
    removeRow: remove,
    handleAddItem,
    handleAddFromComposer,
    handleDuplicateRow,
    handleToggleExpand,
    handleMaterialSelected,
    handleClearMaterial,
    openMaterialModal,
    openCreateMaterialModal,
    handleCreateMaterialSubmit,
    handleGlobalBudgetValueChange,
    handleGlobalCurrencyChange,
    handleToggleGlobalBudget,
    handleGlobalNeededFromChange,
    handleGlobalNeededToChange,
    handleToggleGlobalDates,
    setSingleDateMode,
    handleAttachmentUpload,
    handleRemoveFile,
    handleJumpToIncomplete,
    handleSubmit,
  };
};
